import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { getOperatorConversations } from '@/lib/operator-api/conversations-client';
import type { OperatorConversation } from '@/lib/operator-api/types';

export type ViewMode = 'smart' | 'table';

export function useConversationFilters() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_auth/operator/conversations/' });
  const urlFlowId = (search as any).flowId ?? null;
  const urlFlowStep = (search as any).flowStep ?? null;

  const [viewMode, setViewMode] = useState<ViewMode>('smart');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [state, setState] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const hasActiveFilters = useMemo(
    () => Boolean(phoneNumber || state || dateFrom || dateTo),
    [phoneNumber, state, dateFrom, dateTo],
  );

  const handleReset = useCallback(() => {
    setPhoneNumber('');
    setState('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  }, []);

  const clearFlowFilter = useCallback(() => {
    navigate({
      to: '/operator/conversations',
      search: {},
      replace: true,
    });
  }, [navigate]);

  return {
    viewMode,
    setViewMode,
    phoneNumber,
    setPhoneNumber,
    state,
    setState,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    currentPage,
    setCurrentPage,
    urlFlowId,
    urlFlowStep,
    hasActiveFilters,
    clearFlowFilter,
    handleReset,
  };
}

const LIMIT = 20;

export function useConversationData(options: {
  operatorExists: boolean;
  canViewConversations: boolean;
  viewMode: ViewMode;
  phoneNumber: string;
  state: string;
  dateFrom: string;
  dateTo: string;
  currentPage: number;
  selectedBusinessId: string | null;
}) {
  const {
    operatorExists,
    canViewConversations,
    viewMode,
    phoneNumber,
    state,
    dateFrom,
    dateTo,
    currentPage,
    selectedBusinessId,
  } = options;

  const [conversations, setConversations] = useState<OperatorConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (!operatorExists || !canViewConversations || viewMode !== 'table') {
      return;
    }

    let cancelled = false;

    async function fetchConversations() {
      setLoading(true);
      setError(null);

      try {
        const filters: Record<string, any> = {
          page: currentPage,
          limit: LIMIT,
        };

        if (selectedBusinessId) {
          filters.businessId = selectedBusinessId;
        }
        if (phoneNumber) {
          filters.phoneNumber = phoneNumber;
        }
        if (state) {
          filters.state = state;
        }
        if (dateFrom) {
          filters.dateFrom = dateFrom;
        }
        if (dateTo) {
          filters.dateTo = dateTo;
        }

        const response = await getOperatorConversations(filters);

        if (!cancelled) {
          setConversations(response.conversations);
          setTotal(response.total);
          setTotalPages(Math.ceil(response.total / (response.limit || LIMIT)));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load conversations');
          setConversations([]);
          setTotal(0);
          setTotalPages(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchConversations();

    return () => {
      cancelled = true;
    };
  }, [
    operatorExists,
    canViewConversations,
    viewMode,
    phoneNumber,
    state,
    dateFrom,
    dateTo,
    currentPage,
    selectedBusinessId,
  ]);

  return {
    conversations,
    loading,
    error,
    total,
    totalPages,
  };
}
